import {
    BookmarkBorder,
    CallOutlined,
    CloseOutlined,
    EmailOutlined,
    Facebook,
    HouseOutlined,
    Instagram,
    Language,
    LinkedIn,
    LocationOnOutlined,
    X,
} from '@mui/icons-material';
import { Divider, SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useQueryClient } from '@tanstack/react-query';
import { ReactComponent as BedOutlined } from 'assets/svg/bedrooms.svg';
import { ReactComponent as BathroomsOutlined } from 'assets/svg/feature-bathroom.svg';
import { ReactComponent as Budget } from 'assets/svg/budget.svg';
import { ReactComponent as ConstructedArea } from 'assets/svg/constructed-area.svg';
import { ReactComponent as Commission } from 'assets/svg/percentage-square.svg';
import { ReactComponent as DefaultProperty } from 'assets/svg/default-property.svg';
import { ReactComponent as Urgency } from 'assets/svg/urgency.svg';
import { useSavedOrRemovePropertyRequest } from 'hooks/usePropertyRequests';
import { useScrollLock } from 'hooks/useScrollLock';
import useAddRequestStore from 'store/addRequestStore';
import usePropertyRequestsStore from 'store/propertyRequestsStore';
import { PropertyLead } from 'types/PropertyLead';
import { formatDateForPropertyRequests } from 'util/formatDate';
import { urgencyOptions } from 'util/urgencySelector';

interface Props {
    visible: boolean;
}

export default function RequestDetails({ visible }: Props) {
    const { setPropertyRequestDetailModalVisible } = useAddRequestStore();
    const { lockScroll, unlockScroll } = useScrollLock();
    const { propertyRequestQueryFilter, selectedLead, setSelectedLead, savedRequestsPage, page } = usePropertyRequestsStore();
    const data = useQueryClient().getQueryData(['getPropertyRequests', { ...propertyRequestQueryFilter, page: page + 1 }]) as Record<string, any>;
    const { mutate: savePropertyRequest } = useSavedOrRemovePropertyRequest();
    const savedRequests = useQueryClient().getQueryData(['savedPropertyRequests', savedRequestsPage]) as Record<string, any>;
    const isSaved = savedRequests?.data?.data?.leads?.some((request: Record<string, any>) => request.id === selectedLead.id);

    if (visible) {
        lockScroll();
    } else {
        unlockScroll();
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-50 font-poppins">
            <div className="fixed inset-x-0 bottom-0 default-lg:h-[87vh] h-[97vh] motion-safe:animate-[slideUp_400ms_ease-out_forwards] bg-gray-50 rounded-t-3xl p-4">
                {/* Main Content */}
                <div className=" bg-gray-50 flex default-lg:flex-row flex-col h-full overflow-auto default-lg:overflow-hidden">
                    {/* Left Section - Property Details */}
                    <div
                        className={` ${data?.data?.data?.leads.length >= 2 ? 'default-lg:w-[70%] w-full' : 'w-[100%]'}  px-6 py-4 default-lg:overflow-auto overflow-visible border-r border-gray-200 `}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <h1 className="text-xl font-semibold text-light-black">{selectedLead.title}</h1>
                                <div className=" text-sm mt-2 text-light-black/60">
                                    <span className="text-base font-medium text-light-black/80">
                                        {selectedLead.definedArea ? selectedLead.definedArea : selectedLead.propertyCountry}
                                    </span>{' '}
                                    <span className="font-extrabold">·</span>{' '}
                                    <span className="text-base font-medium text-light-black/80">{selectedLead.propertyType}</span>{' '}
                                    <span className="font-extrabold">·</span> Posted {formatDateForPropertyRequests(selectedLead.createdAt)}
                                </div>
                            </div>
                            <div
                                className={`flex items-center justify-center rounded-lg ${isSaved ? 'bg-my-green' : ''} border border-my-gray p-2  ${data?.data?.data?.leads.length >= 2 ? '' : 'mr-10'}`}
                            >
                                <BookmarkBorder className={`text-center ${isSaved ? 'text-white' : 'text-my-green'}`} />
                                <button
                                    className={` px-3  py-0.5 ${isSaved ? 'text-white' : 'text-my-green'}`}
                                    onClick={() => {
                                        savePropertyRequest({ propertyLeadId: selectedLead.id, action: isSaved ? 'remove' : 'save' });
                                    }}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                        <div className="border border-gray-200  w-full mb-3 " />
                        {/*  */}

                        <div className="mb-6">
                            <h2 className="font-medium text-light-black/60 mb-2">Request Description</h2>
                            <p className="text-light-black/90">{selectedLead.description}</p>
                        </div>

                        <div className="grid  default-lg:grid-cols-3 default-md:grid-cols-2 grid-cols-1 gap-6 mb-8">
                            {selectedLead.definedArea ? (
                                <Details title="Area" value={selectedLead.definedArea} Icon={LocationOnOutlined} />
                            ) : (
                                <>
                                    <Details title="Country" value={selectedLead.propertyCountry} Icon={LocationOnOutlined} />
                                    <Details title="State" value={selectedLead.states?.map((state) => state.state).join(', ')} Icon={LocationOnOutlined} />
                                    <Details title="City" value={selectedLead.cities.map((city) => city.city).join(', ')} Icon={LocationOnOutlined} />
                                    {selectedLead.propertyNeighborhood && selectedLead.propertyNeighborhood.length > 0 && (
                                        <Details title="Neighborhood" value={selectedLead.propertyNeighborhood.join(', ')} Icon={LocationOnOutlined} />
                                    )}
                                </>
                            )}
                            {selectedLead.minBudget !== null && selectedLead.maxBudget !== null && (
                                <Details title="Budget" value={`€${selectedLead.minBudget} - €${selectedLead.maxBudget}`} Icon={Budget} />
                            )}
                            {selectedLead.minConstructionArea !== null && selectedLead.maxConstructionArea !== null && (
                                <Details
                                    title="Constructed area"
                                    value={`${selectedLead.minConstructionArea} - ${selectedLead.maxConstructionArea} m²`}
                                    Icon={ConstructedArea}
                                />
                            )}
                            {selectedLead.minPlotArea !== null && selectedLead.maxPlotArea !== null && (
                                <Details title="Plot area" value={`${selectedLead.minPlotArea} - ${selectedLead.maxPlotArea} m²`} Icon={ConstructedArea} />
                            )}
                            {selectedLead.minBedrooms !== null && selectedLead.maxBedrooms !== null && (
                                <Details title="Approx. Bedrooms" value={`${selectedLead.minBedrooms} - ${selectedLead.maxBedrooms}`} Icon={BedOutlined} />
                            )}
                            {selectedLead.minBathrooms !== null && selectedLead.maxBathrooms !== null && (
                                <Details
                                    title="Approx. Bathrooms"
                                    value={`${selectedLead.minBathrooms} - ${selectedLead.maxBathrooms}`}
                                    Icon={BathroomsOutlined}
                                />
                            )}
                            <Details title="Urgency" value={urgencyOptions.find((option) => option.value === selectedLead.urgency)?.label!} Icon={Urgency} />
                            {selectedLead.furnishing && <Details title="Furnishing" value={selectedLead.furnishing} Icon={HouseOutlined} />}
                            <Details title="Agent Commission" value={selectedLead.commission + '%'} Icon={Commission} />
                            {selectedLead.buyerProfile && <Details title="Buyer Profile" value={selectedLead.buyerProfile} Icon={DefaultProperty} />}
                        </div>

                        <div className="mb-6">
                            <h2 className="font-medium mb-2 text-light-black/60">Amenities</h2>
                            <p className="text-light-black/90">{selectedLead.amenities}</p>
                        </div>

                        <div className="mb-8">
                            <h2 className="font-medium mb-2 text-light-black/60">Other Specific Requirements</h2>
                            <p className="text-light-black/90">{selectedLead.otherSpecificRequirements}</p>
                        </div>

                        <div className="pt-4 rounded-lg border border-my-gray py-8 bg-gray-50/20 px-4">
                            <div className="flex items-center gap-4 mb-4 mt-1.5">
                                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white">
                                    {selectedLead.contactInfo[0].name
                                        ?.split(' ')
                                        .map((word) => word[0])
                                        .join('')
                                        .toUpperCase()}
                                </div>
                                <div>
                                    <div className="text-sm text-light-black/60">Agent Name</div>
                                    <div className="font-medium text-light-black/90">{selectedLead.contactInfo[0].name}</div>
                                </div>
                            </div>
                            <div className="flex default-sm:flex-row flex-col gap-3 default-sm:gap-5">
                                <div className="flex gap-5">
                                    <a
                                        className="flex gap-2 w-1/2 default-sm:w-auto  px-2.5 py-1 bg-white rounded-lg border border-my-gray"
                                        href={`mailto:${selectedLead.contactInfo[0].email}`}
                                    >
                                        <EmailOutlined className="h-6 w-6 my-1.5 text-gray-500" />
                                        <button className="  rounded-lg text-light-black/90">Send Mail</button>
                                    </a>
                                    <a
                                        className="flex gap-2 w-1/2 default-sm:w-auto  px-2.5 py-1 bg-white rounded-lg border border-my-gray"
                                        href={`tel:${selectedLead.contactInfo[0].phone}`}
                                    >
                                        <CallOutlined className="h-6 w-6 my-1.5 text-gray-500" />
                                        <button className="  rounded-lg text-light-black/90">Call Agent</button>
                                    </a>
                                </div>
                                {selectedLead.contactInfo[0].facebook ||
                                selectedLead.contactInfo[0].instagram ||
                                selectedLead.contactInfo[0].linkedin ||
                                selectedLead.contactInfo[0].twitter ||
                                selectedLead.contactInfo[0].website ? (
                                    <>
                                        <Divider flexItem orientation="vertical" />
                                        <div className="flex gap-5 justify-center default-sm:justify-start">
                                            {selectedLead.contactInfo[0].facebook && (
                                                <a
                                                    className="p-2 border border-my-gray rounded-lg self-center text-light-black/80 "
                                                    target="_blank"
                                                    href={`${selectedLead.contactInfo[0].facebook}`}
                                                >
                                                    <Facebook />
                                                </a>
                                            )}
                                            {selectedLead.contactInfo[0].instagram && (
                                                <a
                                                    className="p-2 border border-my-gray rounded-lg self-center text-light-black/80 "
                                                    target="_blank"
                                                    href={`${selectedLead.contactInfo[0].instagram}`}
                                                >
                                                    <Instagram />
                                                </a>
                                            )}
                                            {selectedLead.contactInfo[0].linkedin && (
                                                <a
                                                    className="p-2 border border-my-gray rounded-lg self-center text-light-black/80 "
                                                    target="_blank"
                                                    href={`${selectedLead.contactInfo[0].linkedin}`}
                                                >
                                                    <LinkedIn />
                                                </a>
                                            )}
                                            {selectedLead.contactInfo[0].twitter && (
                                                <a
                                                    className="p-2 border border-my-gray rounded-lg self-center text-light-black/80 "
                                                    target="_blank"
                                                    href={`${selectedLead.contactInfo[0].twitter}`}
                                                >
                                                    <X />
                                                </a>
                                            )}
                                            {selectedLead.contactInfo[0].website && (
                                                <a
                                                    className="p-2 border border-my-gray rounded-lg self-center text-light-black/80 "
                                                    target="_blank"
                                                    href={`${selectedLead.contactInfo[0].website.startsWith('http') ? selectedLead.contactInfo[0].website : `https://${selectedLead.contactInfo[0].website}`}`}
                                                >
                                                    <Language />
                                                </a>
                                            )}
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Right Section - More Requests */}
                    <div
                        className={`${data?.data?.data?.leads.length >= 2 ? 'block  default-lg:w-[30%] w-full' : 'hidden'} px-6 py-4 default-lg:overflow-auto overflow-visible `}
                    >
                        <h2 className="font-semibold text-lg mb-6 text-light-black/90 ">More Requests</h2>
                        {data?.data?.data?.leads
                            ?.filter((l: PropertyLead) => l.id !== selectedLead.id)
                            .slice(0, 5)
                            ?.map((lead: PropertyLead) => (
                                <div key={lead.id} className="mb-6 p-4 border rounded-lg bg-white cursor-pointer" onClick={() => setSelectedLead(lead)}>
                                    <h3 className="font-semibold text-base mb-1">{lead.title}</h3>
                                    <div className="text-sm text-light-black/80 mb-2">
                                        {lead.propertyCountry} · {lead.propertyType}
                                        {lead.propertyNeighborhood && lead.propertyNeighborhood.length > 0 && <> · {lead.propertyNeighborhood.join(', ')}</>}
                                    </div>
                                    <div className="flex gap-4 text-sm mt-5">
                                        <span className="bg-gray-100 p-1.5 text-light-black/70 font-medium text-[12px] rounded">
                                            €{lead.minBudget} - €{lead.maxBudget}
                                        </span>
                                        <span className="bg-gray-100 p-1.5 text-light-black/70 font-medium text-[12px] rounded">
                                            {lead.minConstructionArea} - {lead.maxConstructionArea} m²
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                <button
                    onClick={() => setPropertyRequestDetailModalVisible(false)}
                    className="absolute top-0 default-lg:top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
                >
                    <CloseOutlined />
                </button>
            </div>
        </div>
    );
}

function Details({
    title,
    value,
    Icon,
}: {
    title: string;
    value: string;
    Icon:
        | React.JSXElementConstructor<React.SVGProps<SVGSVGElement>>
        | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
              muiName: string;
          });
}) {
    return (
        <div className=" px-4 py-4 border border-my-gray rounded-lg">
            <Icon className="h-6 w-6 my-1.5 text-gray-500" />
            <div className="text-light-black/50 text-sm mb-1">{title}</div>
            <div className="font-medium text-light-black/80">{value}</div>
        </div>
    );
}
